import React, { useState } from "react";
import {
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Box,
} from "@mui/material";
import { Visibility, VisibilityOff, LockOutlined } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AuthViewController from "../../redux/actions/AuthViewController";
import AuthLayout from "../../components/auth/AuthLayout";
import { BRAND, inputSx, primaryButtonSx } from "../../components/auth/authTheme";

const OtpResetPassword = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { login } = AuthViewController();
  const navigate = useNavigate();
  const [otpVerified, setOtpVerified] = useState(false);
  const otp = watch("otp");

  const onSubmit = (data) => {
    if (!otpVerified) {
      if (otp && otp.length === 6) {
        setOtpVerified(true);
      } else {
        alert("Please enter a valid 6-digit OTP.");
      }
    } else {
      if (data.newPassword !== data.confirmPassword) {
        alert("Passwords do not match.");
        return;
      }
      dispatch(login(data));
    }
  };

  return (
    <AuthLayout
      title="Password reset"
      subtitle="We've sent a code to your email address"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="otp"
          control={control}
          rules={{
            required: "OTP is required",
            minLength: { value: 6, message: "OTP must be 6 digits" },
            maxLength: { value: 6, message: "OTP must be 6 digits" },
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label="Enter 6-digit OTP"
              fullWidth
              variant="outlined"
              sx={{ ...inputSx, mb: 2.5 }}
              error={!!errors.otp}
              helperText={errors.otp?.message}
              inputProps={{ maxLength: 6 }}
            />
          )}
        />

        {otpVerified && (
          <>
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: "New Password is required",
                minLength: { value: 6, message: "Min 6 characters required" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  variant="outlined"
                  sx={{ ...inputSx, mb: 2.5 }}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          sx={{ color: BRAND.muted }}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Please confirm password",
                validate: (value) =>
                  value === watch("newPassword") || "Passwords do not match",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirm Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  sx={{ ...inputSx, mb: 3 }}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disableElevation
          sx={{ ...primaryButtonSx, mb: 2 }}
        >
          {otpVerified ? "Reset Password" : "Verify OTP"}
        </Button>

        <Box sx={{ textAlign: "center" }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate("/login")}
            sx={{
              color: BRAND.orange,
              fontWeight: 600,
              cursor: "pointer",
              border: "none",
              background: "none",
              fontFamily: "inherit",
            }}
          >
            Back to sign in
          </Link>
        </Box>
      </form>
    </AuthLayout>
  );
};

export default OtpResetPassword;
